import { create } from 'zustand';

import {
  WebSocketClient,
  type WebSocketClientOptions,
  type WebSocketStatus,
} from '@/utils/websocket';

export type GlobalWebSocketMessage = unknown;
export type GlobalWebSocketSendMessage = unknown;
export type GlobalWebSocketOptions = WebSocketClientOptions<
  GlobalWebSocketMessage,
  GlobalWebSocketSendMessage
>;

export const webSocketClient = new WebSocketClient<
  GlobalWebSocketMessage,
  GlobalWebSocketSendMessage
>({
  autoConnect: false,
  heartbeatMessage: { type: 'ping' },
});

interface WebSocketStore {
  connectedAt: number;
  lastMessage: GlobalWebSocketMessage | null;
  lastMessageAt: number;
  lastReconnectDelay: number;
  reconnectAttempts: number;
  status: WebSocketStatus;
  configure: (options: GlobalWebSocketOptions) => void;
  connect: () => Promise<void>;
  close: (code?: number, reason?: string) => void;
  reconnect: () => void;
  send: (message: GlobalWebSocketSendMessage, options?: { queue?: boolean }) => boolean;
  clearLastMessage: () => void;
}

export const useWebSocketStore = create<WebSocketStore>(set => ({
  connectedAt: 0,
  lastMessage: null,
  lastMessageAt: 0,
  lastReconnectDelay: 0,
  reconnectAttempts: 0,
  status: webSocketClient.getStatus(),

  configure(options) {
    webSocketClient.configure(options);
  },

  connect() {
    return webSocketClient.connect();
  },

  close(code, reason) {
    webSocketClient.close(code, reason);
  },

  reconnect() {
    webSocketClient.reconnect();
  },

  send(message, options) {
    return webSocketClient.send(message, options);
  },

  clearLastMessage() {
    set({
      lastMessage: null,
      lastMessageAt: 0,
    });
  },
}));

webSocketClient.on('status', ({ status }) => {
  useWebSocketStore.setState({
    status,
    ...(status === 'open' ? { connectedAt: Date.now() } : {}),
  });
});

webSocketClient.on('message', message => {
  useWebSocketStore.setState({
    lastMessage: message,
    lastMessageAt: Date.now(),
  });
});

webSocketClient.on('reconnect', ({ attempt, delay }) => {
  useWebSocketStore.setState({
    lastReconnectDelay: delay,
    reconnectAttempts: attempt,
  });
});

webSocketClient.on('open', () => {
  useWebSocketStore.setState({
    lastReconnectDelay: 0,
    reconnectAttempts: 0,
  });
});

